import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { BookOpen, ClipboardCheck, GraduationCap, Lightbulb } from "lucide-react";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Initial Consultation",
  description: "Begin your journey with a detailed examination and a personalized treatment plan tailored to your specific dental goals and budget.",
  icon: Lightbulb
}, {
  title: "Precision Impressions",
  description: "We capture high-resolution impressions of your jaw and gums to ensure your new dentures fit securely and comfortably for daily use.",
  icon: ClipboardCheck
}, {
  title: "Custom Trial Fitting",
  description: "Preview your new smile with a wax model fitting. We refine the aesthetics and bite alignment until you are completely satisfied with the look.",
  icon: BookOpen
}, {
  title: "Final Placement",
  description: "Receive your custom-crafted dentures. Our specialists perform final adjustments and provide care guidance for lasting comfort and functionality.",
  icon: GraduationCap
}];
function TimelineItem({
  item,
  index,
  isLast
}: {
  item: typeof steps[0];
  index: number;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <div data-section-id="53451" ref={ref} data-index={index} className={cn("flex gap-6 md:gap-10 transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[30px]")}>
      {}
      <div className="flex flex-col items-center">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-background shadow-md z-10">
          <item.icon className="h-6 w-6 text-primary stroke-[2px]" />
        </div>
        {}
        {!isLast && <div className="h-full w-px border-l-2 border-dashed border-primary/20 my-2" />}
      </div>

      {}
      <div className="pb-16 pt-2">
        <h3 className="font-serif text-2xl md:text-3xl font-semibold text-foreground mb-3">
          {item.title}
        </h3>
        <p className="font-sans text-muted-foreground leading-relaxed max-w-xl text-lg">
          {item.description}
        </p>
      </div>
    </div>;
}
export default function HowNvAcademyWorks() {
  return <section id="denture-procedure-steps" className="bg-secondary py-24 px-6 md:px-12 lg:px-24 overflow-hidden">
      <div className="max-w-4xl mx-auto">
        <div className="mb-16 text-center md:text-left">
          <h2 className="font-serif text-4xl md:text-5xl text-primary mb-6">
            Your Path to a Perfect Smile
          </h2>
          <p className="font-sans text-muted-foreground max-w-2xl text-lg">
            A structured, professional journey designed to restore your confidence and oral health through our proven dental methodology.
          </p>
        </div>

        <div className="space-y-0">
          {steps.map((step, i) => <TimelineItem key={i} item={step} index={i} isLast={i === steps.length - 1} />)}
        </div>

        <div className="mt-8 flex justify-center md:justify-start pl-0 md:pl-[88px]">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-105 transition-all duration-300 shadow-xl rounded-full px-10 py-7 text-lg font-medium bg-gradient-to-r from-primary to-primary/90">
            <Link to="/ContactUsPage">
              Book Your Consultation
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}